import { useEffect, useMemo, useState } from 'react';
import type { StoreApi, UseBoundStore } from 'zustand';
import { PrefSegmentedControl, PrefSwitch } from '../components/PrefControls';
import type { ThemeFamily, ThemePreferences } from './themeCatalog';
import {
  FLUENT_THEME_OPTIONS,
  GENERIC_THEME_OPTIONS,
  MATERIAL_THEME_OPTIONS
} from './themeCatalog';
import type { ThemeStoreState } from './createThemeStore';
import { preferencesEqual } from './themeResolve';
import './themePreferences.css';

type ThemeStore = UseBoundStore<StoreApi<ThemeStoreState>>;

const FAMILY_ITEMS = [
  { text: 'Material', value: 'material' as ThemeFamily },
  { text: 'Generic', value: 'generic' as ThemeFamily },
  { text: 'Fluent', value: 'fluent' as ThemeFamily }
];

export interface ThemePreferencesPanelProps {
  useThemeStore: ThemeStore;
}

export function ThemePreferencesPanel({ useThemeStore }: ThemePreferencesPanelProps) {
  const family = useThemeStore((s) => s.family);
  const materialAccent = useThemeStore((s) => s.materialAccent);
  const genericPalette = useThemeStore((s) => s.genericPalette);
  const genericCompact = useThemeStore((s) => s.genericCompact);
  const fluentVariant = useThemeStore((s) => s.fluentVariant);
  const fluentCompact = useThemeStore((s) => s.fluentCompact);
  const mode = useThemeStore((s) => s.mode);
  const commitPreferences = useThemeStore((s) => s.commitPreferences);

  const saved = useMemo<ThemePreferences>(
    () => ({
      family,
      materialAccent,
      genericPalette,
      genericCompact,
      fluentVariant,
      fluentCompact,
      mode
    }),
    [
      family,
      materialAccent,
      genericPalette,
      genericCompact,
      fluentVariant,
      fluentCompact,
      mode
    ]
  );

  const [draft, setDraft] = useState<ThemePreferences>(saved);

  useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const dirty = useMemo(() => !preferencesEqual(draft, saved), [draft, saved]);

  const familyKey = draft.family;

  function updateDraft(patch: Partial<ThemePreferences>) {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function onSave() {
    commitPreferences(draft);
  }

  return (
    <section
      className="theme-preferences theme-preferences--first theme-preferences--appearance"
      aria-labelledby="theme-preferences-heading"
    >
      <h3 id="theme-preferences-heading" className="theme-preferences__title">
        Appearance
      </h3>
      <p className="theme-preferences__hint">
        Choose a theme family and color. Changes apply when you save. Use the toolbar moon/sun for
        light/dark on Material and Fluent, or Generic Light/Dark only.
      </p>

      <div className="theme-preferences__body">
        <div className="theme-preferences__section">
          <span className="theme-preferences__label">Theme family</span>
          <PrefSegmentedControl
            className="theme-preferences__family-group"
            items={FAMILY_ITEMS}
            value={draft.family}
            aria-label="Theme family"
            onChange={(next) => updateDraft({ family: next })}
          />
        </div>

        {familyKey === 'material' ? (
          <div className="theme-preferences__section">
            <span className="theme-preferences__label">Accent color</span>
            <div className="theme-preferences__grid" role="list">
              {MATERIAL_THEME_OPTIONS.map((opt) => (
                <button
                  key={opt.accent}
                  type="button"
                  role="listitem"
                  className={`theme-preferences__tile${draft.materialAccent === opt.accent ? ' theme-preferences__tile--selected' : ''}`}
                  onClick={() => updateDraft({ materialAccent: opt.accent })}
                  aria-pressed={draft.materialAccent === opt.accent}
                >
                  <span
                    className="theme-preferences__swatch"
                    style={{ background: opt.swatchColor }}
                    aria-hidden
                  />
                  <span className="theme-preferences__tile-label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {familyKey === 'fluent' ? (
          <>
            <div className="theme-preferences__section">
              <span className="theme-preferences__label">Fluent style</span>
              <div className="theme-preferences__grid theme-preferences__grid--fluent" role="list">
                {FLUENT_THEME_OPTIONS.map((opt) => (
                  <button
                    key={opt.variant}
                    type="button"
                    role="listitem"
                    className={`theme-preferences__tile${draft.fluentVariant === opt.variant ? ' theme-preferences__tile--selected' : ''}`}
                    onClick={() =>
                      updateDraft({
                        fluentVariant: opt.variant,
                        ...(opt.variant === 'red-storm' ? { mode: 'dark' as const, fluentCompact: false } : {})
                      })
                    }
                    aria-pressed={draft.fluentVariant === opt.variant}
                  >
                    <span
                      className="theme-preferences__swatch"
                      style={{ background: opt.swatchColor }}
                      aria-hidden
                    />
                    <span className="theme-preferences__tile-label">{opt.label}</span>
                  </button>
                ))}
              </div>
              {draft.fluentVariant === 'red-storm' ? (
                <p className="theme-preferences__hint theme-preferences__hint--tight">
                  Storm Red is a dark-only theme.
                </p>
              ) : null}
            </div>
            <div className="theme-preferences__section">
              <span className="theme-preferences__label">Compact density</span>
              <div className="theme-preferences__compact-row">
                <PrefSwitch
                  checked={draft.fluentCompact}
                  disabled={draft.fluentVariant === 'red-storm'}
                  onChange={(checked) => updateDraft({ fluentCompact: checked })}
                  aria-label="Fluent compact density"
                />
                <span className="theme-preferences__hint theme-preferences__hint--inline">
                  Smaller padding and fonts (Fluent themes only)
                </span>
              </div>
            </div>
          </>
        ) : null}

        {familyKey === 'generic' ? (
          <>
            <div className="theme-preferences__section">
              <span className="theme-preferences__label">Color scheme</span>
              <div className="theme-preferences__grid" role="list">
                {GENERIC_THEME_OPTIONS.map((opt) => (
                  <button
                    key={opt.palette}
                    type="button"
                    role="listitem"
                    className={`theme-preferences__tile${draft.genericPalette === opt.palette ? ' theme-preferences__tile--selected' : ''}`}
                    onClick={() => updateDraft({ genericPalette: opt.palette })}
                    aria-pressed={draft.genericPalette === opt.palette}
                  >
                    <span
                      className="theme-preferences__swatch"
                      style={{ background: opt.swatchColor }}
                      aria-hidden
                    />
                    <span className="theme-preferences__tile-label">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="theme-preferences__section">
              <span className="theme-preferences__label">Compact density</span>
              <div className="theme-preferences__compact-row">
                <PrefSwitch
                  checked={draft.genericCompact}
                  onChange={(checked) => updateDraft({ genericCompact: checked })}
                  aria-label="Generic compact density"
                />
                <span className="theme-preferences__hint theme-preferences__hint--inline">
                  Smaller padding and fonts (Generic themes only)
                </span>
              </div>
            </div>
          </>
        ) : null}
      </div>

      <footer className="theme-preferences__footer">
        <button
          type="button"
          className="theme-preferences__save-btn"
          disabled={!dirty}
          onClick={onSave}
        >
          Save appearance
        </button>
      </footer>
    </section>
  );
}
